/**
 * Account and password validation.
 *
 * These rules are advisory. The server runs its own check and its answer is the
 * one that counts, so anything enforced here has to match what the server
 * enforces -- see `passwordPolicyService`, which fetches the real policy. The
 * password length used to be hardcoded at 6 here while the server demanded 8,
 * and the only sign of it was an opaque "Invalid request" on submit.
 */

export interface ValidationResult {
  isValid: boolean
  error?: string
  /** Values the message interpolates, e.g. `{ min: 8 }` for a length rule. */
  params?: Record<string, string | number>
}

/**
 * The password rules to apply. Mirrors the server's /auth/password-policy
 * response; the defaults match the server's own defaults so a form still
 * validates sensibly if the policy could not be fetched.
 */
export interface PasswordPolicy {
  minLength: number
  maxLength: number
  requireLetter: boolean
  requireDigit: boolean
  /**
   * Whether the server also refuses passwords that are merely guessable: a
   * breach-list word with padding, the account's own name, one key held down.
   * Nothing here can reproduce that check, so the form says it exists and lets
   * the server be the one to answer.
   */
  blocksCommonPasswords?: boolean
  /** Zero, and shown as a decision rather than an omission. */
  expiresAfterDays?: number
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 14,
  maxLength: 72,
  requireLetter: true,
  requireDigit: true,
  blocksCommonPasswords: true,
  expiresAfterDays: 0,
}

/**
 * Account rules:
 * - letters, digits, underscore and hyphen only
 * - no whitespace
 * - 3-20 characters
 * - must start with a letter or digit
 */
export const validateAccount = (account: string): ValidationResult => {
  if (!account || account.trim() === '') {
    return { isValid: false, error: 'validation.account.required' }
  }

  if (account.length < 3) {
    return { isValid: false, error: 'validation.account.tooShort' }
  }

  if (account.length > 20) {
    return { isValid: false, error: 'validation.account.tooLong' }
  }

  if (/\s/.test(account)) {
    return { isValid: false, error: 'validation.account.noWhitespace' }
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(account)) {
    return { isValid: false, error: 'validation.account.invalidCharacters' }
  }

  if (!/^[a-zA-Z0-9]/.test(account)) {
    return { isValid: false, error: 'validation.account.mustStartWithAlphanumeric' }
  }

  return { isValid: true }
}

/**
 * Password rules, taken from the policy the server published:
 * - at least `minLength`, at most `maxLength` characters
 * - no whitespace and no control characters
 * - a letter and a digit, when the policy asks for them
 *
 * The server applies more than this. It also refuses a password built around
 * the account's own name, one drawn from a breach list, and one that is the
 * same few characters repeated. Those need the account and the list, so they
 * are not duplicated here: a password that passes this check can still come
 * back rejected, and the server's message is the one that explains why.
 */
export const validatePassword = (
  password: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): ValidationResult => {
  if (!password) {
    return { isValid: false, error: 'validation.password.required' }
  }

  if (password.length < policy.minLength) {
    return {
      isValid: false,
      error: 'validation.password.tooShort',
      params: { min: policy.minLength },
    }
  }

  if (password.length > policy.maxLength) {
    return {
      isValid: false,
      error: 'validation.password.tooLong',
      params: { max: policy.maxLength },
    }
  }

  if (/\s/.test(password)) {
    return { isValid: false, error: 'validation.password.noWhitespace' }
  }

  if (policy.requireLetter || policy.requireDigit) {
    const hasLetter = /[a-zA-Z]/.test(password)
    const hasNumber = /[0-9]/.test(password)

    if ((policy.requireLetter && !hasLetter) || (policy.requireDigit && !hasNumber)) {
      return { isValid: false, error: 'validation.password.requireAlphanumeric' }
    }
  }

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(password)) {
    return { isValid: false, error: 'validation.password.invalidCharacters' }
  }

  return { isValid: true }
}

/**
 * Display name rules:
 * - not blank
 * - 1-50 characters after trimming
 * - letters in any script, digits and spaces are all fine
 */
export const validateDisplayName = (name: string): ValidationResult => {
  if (!name || name.trim() === '') {
    return { isValid: false, error: 'validation.name.required' }
  }

  const trimmedName = name.trim()
  if (trimmedName.length < 1) {
    return { isValid: false, error: 'validation.name.required' }
  }

  if (trimmedName.length > 50) {
    return { isValid: false, error: 'validation.name.tooLong' }
  }

  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(name)) {
    return { isValid: false, error: 'validation.name.invalidCharacters' }
  }

  return { isValid: true }
}

/** The confirmation field has to be filled in and has to match. */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword) {
    return { isValid: false, error: 'validation.confirmPassword.required' }
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'validation.confirmPassword.mismatch' }
  }

  return { isValid: true }
}

/** General helpers. */

export const containsOnlyAllowedChars = (str: string, pattern: RegExp): boolean => {
  return pattern.test(str)
}

export const hasWhitespace = (str: string): boolean => {
  return /\s/.test(str)
}

export const isLengthInRange = (str: string, min: number, max: number): boolean => {
  return str.length >= min && str.length <= max
}

export const sanitizeInput = (input: string): string => {
  return input.trim()
}

export default {
  validateAccount,
  validatePassword,
  validateDisplayName,
  validatePasswordConfirmation,
  containsOnlyAllowedChars,
  hasWhitespace,
  isLengthInRange,
  sanitizeInput,
}
